<template>
	<div>
		<div v-if="hasAnyData" class="chart-wrapper">
			<div class="graph-container">
				<apexchart type="line" height="100%" :options="chartOptions" :series="chartSeries" />
			</div>
			<div class="top-mentions-container" v-if="hasTopCitationsData">
				<p>{{ t('dashboard_geo.share_voice.top_citations') }} ({{ aiTypeName }})</p>
				<div class="top-mentions-chart">
					<apexchart type="bar" height="100%" :options="barChartOptions" :series="barChartSeries" />
				</div>
				<div class="top-mentions-actions">
					<button class="show-all-btn" :disabled="!hasAllTopCitations"
						@click="showAllTopCitationsModal = true">{{ t('dashboard_geo.share_voice.show_all') }}</button>
				</div>
			</div>
		</div>
		<p class="no-data" v-else>{{ t('no_data') }}</p>

		<Modal :showModal="showAllTopCitationsModal" @close-modal="showAllTopCitationsModal = false" width="620px"
			height="auto">
			<template #title>{{ t('dashboard_geo.share_voice.top_citations') }} ({{ aiTypeName }})</template>
			<template #content>
				<div class="top-citations-modal-table-wrap">
					<table class="top-citations-modal-table">
						<thead>
							<tr>
								<th>{{ t('dashboard_geo.share_voice.table.source') }}</th>
								<th>{{ t('dashboard_geo.share_voice.table.citations') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in allTopCitations" :key="`citation-${item.domain}`">
								<td>{{ item.domain }}</td>
								<td>{{ Number(item.count || 0) }}</td>
							</tr>
						</tbody>
					</table>
					<p v-if="allTopCitations.length === 0" class="no-data-inline">{{
						t('dashboard_geo.share_voice.no_sources_found') }}</p>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import Modal from '@/components/utilities/modal/modal.vue';
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js';
import { useMarketStore } from '@/store/global/market';
import { useSubNavigationStore } from '@/store/global/subNavigation';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const geoStore = useDashboardGeoStore();
const marketStore = useMarketStore();
const { selectedMarket } = storeToRefs(marketStore);
const { scores } = storeToRefs(geoStore);
const subNavigationStore = useSubNavigationStore();
const { aiTypeOptionsDashboard, selectedAiTypeDashboard } = storeToRefs(subNavigationStore);
const showAllTopCitationsModal = ref(false);
const TOP_CITATIONS_LIMIT = 5;

const aiTypeInfo = computed(() => {
	const selected = aiTypeOptionsDashboard.value.find(option => option.id === selectedAiTypeDashboard.value);
	return selected ? { name: selected.name, key: selected.id } : { name: '', key: '' };
});

const aiTypeName = computed(() => aiTypeInfo.value.name);
const aiTypeKey = computed(() => aiTypeInfo.value.key);

const chatgptData = computed(() => {
	const evolutionData = scores.value?.chatgpt?.share_of_voice_evolution || {};
	return Object.entries(evolutionData)
		.map(([date, value]) => ({ date, value: value || 0 }))
		.sort((a, b) => new Date(a.date) - new Date(b.date));
});

const globalData = computed(() => {
	const evolutionData = scores.value?.global?.share_of_voice_evolution || {};
	return Object.entries(evolutionData)
		.map(([date, value]) => ({ date, value: value || 0 }))
		.sort((a, b) => new Date(a.date) - new Date(b.date));
});

const aiModeData = computed(() => {
	const evolutionData = scores.value?.ai_mode?.share_of_voice_evolution || {};
	return Object.entries(evolutionData)
		.map(([date, value]) => ({ date, value: value || 0 }))
		.sort((a, b) => new Date(a.date) - new Date(b.date));
});

const aiOverviewData = computed(() => {
	const evolutionData = scores.value?.ai_overview?.share_of_voice_evolution || {};
	return Object.entries(evolutionData)
		.map(([date, value]) => ({ date, value: value || 0 }))
		.sort((a, b) => new Date(a.date) - new Date(b.date));
});

const perplexityData = computed(() => {
	const evolutionData = scores.value?.perplexity?.share_of_voice_evolution || {};
	return Object.entries(evolutionData)
		.map(([date, value]) => ({ date, value: value || 0 }))
		.sort((a, b) => new Date(a.date) - new Date(b.date));
});

// Dynamically get top URLs based on selectedAiTypeDashboard
const currentTopUrl = computed(() => {
	const aiType = selectedAiTypeDashboard.value;
	return scores.value?.[aiType]?.top_url || [];
});
const topCitations = computed(() => (Array.isArray(currentTopUrl.value) ? currentTopUrl.value : []));
const topCitationsPreview = computed(() => topCitations.value.slice(0, TOP_CITATIONS_LIMIT));
const allTopCitations = computed(() => topCitations.value);
const hasAllTopCitations = computed(() => allTopCitations.value.length > 0);

const hasAnyData = computed(() => {
	return overlappingDates.value.length > 0;
});

const formatDate = dateString => {
	const date = new Date(dateString);
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const year = date.getFullYear().toString().slice(-2);
	return `${day}/${month}/${year}`;
};

const overlappingDates = computed(() => {
	const chatDates = new Set(chatgptData.value.map(item => item.date));
	const globalDates = new Set(globalData.value.map(item => item.date));
	const aiModeDates = new Set(aiModeData.value.map(item => item.date));
	const aiOverviewDates = new Set(aiOverviewData.value.map(item => item.date));
	const perplexityDates = new Set(perplexityData.value.map(item => item.date));

	const allDates = new Set([...chatDates, ...globalDates, ...aiModeDates, ...aiOverviewDates, ...perplexityDates]);

	if (allDates.size === 0) {
		return [];
	}

	return Array.from(allDates).sort((a, b) => new Date(a) - new Date(b));
});

const chartSeries = computed(() => {
	if (overlappingDates.value.length === 0) {
		return [];
	}

	const series = [];

	// Global data
	if (globalData.value.length > 0) {
		const gData = overlappingDates.value.map(date => {
			const dataPoint = globalData.value.find(item => item.date === date);
			return dataPoint ? dataPoint.value * 100 : null;
		});

		series.push({
			name: t('dashboard_geo.share_voice.global_evolution'),
			data: gData,
			color: 'var(--vert-500)',
		});
	}

	// ChatGPT data
	if (chatgptData.value.length > 0) {
		const chatData = overlappingDates.value.map(date => {
			const dataPoint = chatgptData.value.find(item => item.date === date);
			return dataPoint ? dataPoint.value * 100 : null;
		});

		series.push({
			name: t('dashboard_geo.share_voice.chatgpt_evolution'),
			data: chatData,
			color: 'var(--jaune-500)',
		});
	}

	// AI Mode data
	if (aiModeData.value.length > 0) {
		const aiModeDataPoints = overlappingDates.value.map(date => {
			const dataPoint = aiModeData.value.find(item => item.date === date);
			return dataPoint ? dataPoint.value * 100 : null;
		});

		series.push({
			name: t('dashboard_geo.share_voice.ai_mode_evolution'),
			data: aiModeDataPoints,
			color: 'var(--violet-500)',
		});
	}

	// AI Overview data
	if (aiOverviewData.value.length > 0) {
		const aiOverviewDataPoints = overlappingDates.value.map(date => {
			const dataPoint = aiOverviewData.value.find(item => item.date === date);
			return dataPoint ? dataPoint.value * 100 : null;
		});

		series.push({
			name: 'AI Overview Evolution',
			data: aiOverviewDataPoints,
			color: 'var(--bleu-500)',
		});
	}

	// Perplexity data
	if (perplexityData.value.length > 0) {
		const perplexityDataPoints = overlappingDates.value.map(date => {
			const dataPoint = perplexityData.value.find(item => item.date === date);
			return dataPoint ? dataPoint.value * 100 : null;
		});

		series.push({
			name: t('dashboard_geo.share_voice.perplexity_evolution'),
			data: perplexityDataPoints,
			color: 'var(--orange-500)',
		});
	}

	return series;
});

const chartYAxisBounds = computed(() => {
	const values = [];
	chartSeries.value.forEach(series => {
		const data = Array.isArray(series?.data) ? series.data : [];
		data.forEach(value => {
			const numeric = Number(value);
			if (Number.isFinite(numeric)) {
				values.push(numeric);
			}
		});
	});

	if (!values.length) {
		return { min: 0, max: 100 };
	}

	const minValue = Math.min(...values);
	const maxValue = Math.max(...values);

	return {
		min: Math.max(0, minValue - 5),
		max: Math.min(100, maxValue + 5),
	};
});

const markerSize = computed(() => (overlappingDates.value.length === 1 ? 8 : 0));

const chartOptions = computed(() => ({
	chart: {
		type: 'line',
		toolbar: {
			show: true,
		},
		zoom: {
			enabled: true,
		},
		redrawOnParentResize: false,
		redrawOnWindowResize: false,
	},
	stroke: {
		curve: 'smooth',
		width: 3,
	},
	markers: {
		size: markerSize.value,
		hover: {
			size: markerSize.value,
		},
	},
	xaxis: {
		categories: overlappingDates.value.map(date => formatDate(date)),
		type: 'category',
	},
	yaxis: {
		min: chartYAxisBounds.value.min,
		max: chartYAxisBounds.value.max,
		labels: {
			formatter: function (value) {
				return value !== null && value !== undefined ? Math.round(value) + '%' : '';
			},
		},
	},
	legend: {
		position: 'top',
		horizontalAlign: 'left',
		markers: {
			width: 12,
			height: 12,
			radius: 6,
		},
	},
	tooltip: {
		shared: true,
		intersect: false,
		y: {
			formatter: function (value) {
				return value !== null && value !== undefined ? value.toFixed(2) + '%' : 'No data';
			},
		},
		x: {
			formatter: function (value, { dataPointIndex }) {
				const date = overlappingDates.value[dataPointIndex];
				return date ? formatDate(date) : value;
			},
		},
	},
	grid: {
		borderColor: 'var(--gris-50)',
	},
	plotOptions: {
		line: {
			dataLabels: {
				enabled: false,
			},
		},
	},
}));

const barChartSeries = computed(() => {
	const data = topCitationsPreview.value.map(item => Number(item?.count || 0));
	return [
		{
			data,
		},
	];
});

const hasTopCitationsData = computed(() => {
	const series = barChartSeries.value[0];
	const data = Array.isArray(series?.data) ? series.data : [];
	return data.some(value => Number(value) > 0);
});

const barChartOptions = computed(() => ({
	chart: {
		type: 'bar',
		toolbar: {
			show: false,
		},
		redrawOnParentResize: false,
		redrawOnWindowResize: false,
	},
	plotOptions: {
		bar: {
			horizontal: true,
			borderRadius: 4,
			distributed: true,
			dataLabels: {
				position: 'top',
			},
		},
	},
	dataLabels: {
		enabled: true,
		offsetX: -10,
		style: {
			fontSize: '12px',
			colors: ['#fff'],
		},
	},
	stroke: {
		show: true,
		width: 1,
		colors: ['#fff'],
	},
	tooltip: {
		shared: false,
		intersect: false,
		y: {
			formatter: function (value) {
				return value + ' citations';
			},
			title: {
				formatter: function () {
					return '';
				},
			},
		},
	},
	xaxis: {
		categories: topCitationsPreview.value.map(item => item.domain),
		labels: {
			show: false,
		},
	},
	colors: ['var(--bleu-400)', 'var(--vert-400)', 'var(--rouge-400)', 'var(--jaune-400)', 'var(--violet-400)'],
	grid: {
		borderColor: 'var(--gris-50)',
	},
	legend: {
		show: false,
	},
}));

const getSelectedDate = () => {
	const selectedDates = JSON.parse(localStorage.getItem('dashboardSelectedDates') || 'null');
	if (selectedDates) {
		const dateToUse = selectedDates.to;

		if (dateToUse && dateToUse.includes('-')) {
			const parts = dateToUse.split('-');
			if (parts.length === 3 && parts[0].length === 2) {
				return `${parts[2]}-${parts[1]}-${parts[0]}`;
			}
		}

		return dateToUse;
	}
	return null;
};

const refreshData = async () => {
	if (selectedMarket.value) {
		try {
			const selectedDate = getSelectedDate();
			await geoStore.fetchAllGeoData(selectedMarket.value, selectedDate);
		} catch (error) {
			console.error('Error refreshing graph data:', error);
		}
	}
};

watch(
	() => {
		const selectedDates = JSON.parse(localStorage.getItem('dashboardSelectedDates') || 'null');
		return selectedDates ? `${selectedDates.from}-${selectedDates.to}` : null;
	},
	(newDateRange, oldDateRange) => {
		if (newDateRange !== oldDateRange && selectedMarket.value) {
			refreshData();
		}
	},
);

watch(selectedMarket, () => {
	refreshData();
});
</script>

<style lang="scss" scoped>
.chart-wrapper {
	height: 100%;
	max-height: 500px;
	display: flex;
	gap: 2rem;
	padding: 1rem;
	overflow: hidden;
	min-height: 400px;
}

.no-data {
	color: var(--gris-600);
	font-style: italic;
	padding: 1.4rem;
	font-size: 1.2rem;
	height: 400px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.no-data-inline {
	color: var(--gris-600);
	font-style: italic;
	padding: 0.6rem 0;
}

.graph-container {
	width: 65%;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.top-mentions-container {
	flex: 0 0 35%;
	min-height: 0;
	overflow: visible;
	display: flex;
	flex-direction: column;
}

.top-mentions-chart {
	flex: 1 1 auto;
	min-height: 220px;
	overflow: hidden;
}

.top-mentions-actions {
	margin-top: 0.6rem;
	display: flex;
	justify-content: center;
	flex: 0 0 auto;
}

.show-all-btn {
	border: 1px solid var(--gris-200);
	background: var(--blanc);
	color: var(--bleu-500);
	padding: 0.42rem 0.72rem;
	border-radius: 0.55rem;
	font-size: 0.86rem;
	font-weight: 600;
	cursor: pointer;

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
}

.top-citations-modal-table-wrap {
	overflow: auto;
}

.top-citations-modal-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.9rem;
}

.top-citations-modal-table th,
.top-citations-modal-table td {
	padding: 0.55rem 0.45rem;
	border-bottom: 1px solid var(--gris-100);
	text-align: left;
}

.top-citations-modal-table th:last-child,
.top-citations-modal-table td:last-child {
	text-align: right;
}
</style>

<style>
.apexcharts-toolbar {
	z-index: 0 !important;
}

.apexcharts-canvas {
	max-height: 100% !important;
}
</style>
